import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Play, ArrowRight, Flag, RotateCcw, Zap, Trophy, Timer } from 'lucide-react';
import { useAcademyStore } from '../../store/academyStore';

const TRACK_LENGTH = 12;

type Level = {
    id: number;
    description: string;
    codeTemplate: string;
    goalSteps: number;
    hint: string;
    startCode: string; // The part before the input
    endCode: string; // The part after the input
};

const LEVELS: Level[] = [
    {
        id: 1,
        description: "The finish line is 5 steps away. Set the loop to run 5 times!",
        codeTemplate: "i < ?",
        goalSteps: 5,
        hint: "Type 5 into the box.",
        startCode: "for (let i = 0; i < ",
        endCode: "; i++) {\n  moveCar();\n}"
    },
    {
        id: 2,
        description: "Longer track! It's 10 steps this time.",
        codeTemplate: "i < ?",
        goalSteps: 10,
        hint: "We need 10 iterations.",
        startCode: "for (let i = 0; i < ",
        endCode: "; i++) {\n  moveCar();\n}"
    },
    {
        id: 3,
        description: "We start at i = 2. The finish is at step 8. How many loop cycles do we need to reach it?",
        codeTemplate: "i < ?",
        goalSteps: 8, // But visually it might be different if we simulated i starting at 2. 
        // For simplicity, let's keep the game mechanic consistent: Input controls number of moves.
        hint: "We need to move 8 times. i starts at 0.",
        startCode: "for (let i = 0; i < ",
        endCode: "; i++) {\n  moveCar();\n}"
    }
];

export const LoopRacer = () => {
    const { completeModule } = useAcademyStore();
    const [currentLevel, setCurrentLevel] = useState(0);
    const [inputValue, setInputValue] = useState("");
    const [carPosition, setCarPosition] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const [status, setStatus] = useState<'idle' | 'running' | 'success' | 'crash'>('idle');
    const [logs, setLogs] = useState<string[]>([]);

    const level = LEVELS[currentLevel];

    useEffect(() => {
        resetLevel();
    }, [currentLevel]);

    const resetLevel = () => {
        setInputValue("");
        setCarPosition(0);
        setIsRunning(false);
        setStatus('idle');
        setLogs([]);
    };

    const runLoop = async () => {
        if (isRunning) return;

        const steps = parseInt(inputValue);
        if (isNaN(steps) || steps < 0) {
            alert("Please enter a valid positive number.");
            return;
        }

        setIsRunning(true);
        setStatus('running');
        setLogs([]);
        setCarPosition(0);

        // Simulation
        for (let i = 0; i < steps; i++) {
            await new Promise(r => setTimeout(r, 500)); // Delay for animation
            setCarPosition(prev => prev + 1);
            setLogs(prev => [...prev, `Loop iteration: ${i + 1}, Car moved!`]);
        }

        await new Promise(r => setTimeout(r, 500));

        if (steps === level.goalSteps) {
            setStatus('success');
            if (currentLevel === LEVELS.length - 1) {
                completeModule('loops-arrays', 100);
            }
        } else if (steps < level.goalSteps) {
            setStatus('idle'); // Just stopped short
            setLogs(prev => [...prev, "Stopped too early! Try a higher number."]);
        } else {
            setStatus('crash');
            setLogs(prev => [...prev, "CRASH! You went too far!"]);
        }

        setIsRunning(false);
    };

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-white flex flex-col">
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-green-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    <span className="text-slate-400 font-bold">Level {currentLevel + 1} of {LEVELS.length}</span>
                    <div className="flex gap-1">
                        {LEVELS.map((l, i) => (
                            <div
                                key={l.id}
                                className={`w-3 h-3 rounded-full ${i < currentLevel ? 'bg-green-500' : i === currentLevel ? 'bg-yellow-400 animate-pulse' : 'bg-slate-700'}`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex-1 flex flex-col lg:flex-row h-[calc(100vh-80px)]">

                {/* Editor Panel (Left) */}
                <div className="w-full lg:w-1/3 bg-slate-800 p-8 flex flex-col border-r border-slate-700 shadow-xl z-10">
                    <div className="mb-6">
                        <h1 className="text-3xl font-black text-yellow-400 mb-2 font-comic tracking-wide italic">Loop Racer 🏎️</h1>
                        <p className="text-slate-300 leading-relaxed text-lg">{level.description}</p>
                    </div>

                    <div className="flex-1 flex flex-col justify-center">
                        <div className="bg-slate-900 rounded-xl p-6 font-mono text-lg border border-slate-700 shadow-inner relative overflow-hidden group">
                            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                                <CodeIcon size={64} />
                            </div>

                            <div className="text-purple-400 mb-2">{level.startCode}</div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    className="bg-slate-800 border-2 border-slate-600 focus:border-yellow-400 rounded px-3 py-1 w-24 text-white outline-none text-center font-bold"
                                    placeholder="?"
                                    disabled={isRunning}
                                />
                            </div>
                            <div className="text-purple-400 mt-2 whitespace-pre">{level.endCode}</div>
                        </div>

                        {/* Logs */}
                        <div className="mt-4 h-40 bg-black/50 rounded-xl p-4 font-mono text-xs overflow-y-auto border border-white/5 text-green-400/80">
                            {logs.length === 0 ? <span className="text-slate-600 italic">// Engine logs will appear here...</span> : logs.map((log, i) => (
                                <div key={i}>{log}</div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 flex gap-4">
                        <button
                            onClick={resetLevel}
                            className="btn bg-slate-700 text-slate-300 hover:bg-slate-600"
                            disabled={isRunning}
                        >
                            <RotateCcw size={18} />
                        </button>

                        {status === 'success' ? (
                            <motion.button
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                onClick={() => {
                                    if (currentLevel < LEVELS.length - 1) {
                                        setCurrentLevel(l => l + 1);
                                    }
                                }}
                                className="btn flex-1 bg-green-500 text-white hover:bg-green-600 shadow-lg shadow-green-500/30"
                            >
                                Next Race <ArrowRight size={18} />
                            </motion.button>
                        ) : (
                            <button
                                onClick={runLoop}
                                disabled={isRunning || !inputValue}
                                className={`btn flex-1 flex items-center justify-center gap-2 font-bold ${isRunning ? 'bg-yellow-500 text-slate-900' : 'bg-blue-500 text-white hover:bg-blue-400'}`}
                            >
                                {isRunning ? 'Running...' : 'Start Engine'} <Play size={18} fill="currentColor" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Visual Track (Right) */}
                <div className="w-full lg:w-2/3 bg-slate-900 relative overflow-hidden flex flex-col items-center justify-center p-12">
                    {/* Background Grid Pattern */}
                    <div className="absolute inset-0 opacity-20"
                        style={{
                            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
                            backgroundSize: '40px 40px'
                        }}
                    />

                    {/* Track Container */}
                    <div className="relative w-full max-w-4xl h-64">
                        {/* Road */}
                        <div className="absolute top-1/2 left-0 w-full h-32 -translate-y-1/2 bg-slate-800 border-y-4 border-slate-600 flex items-center">
                            {/* Lane Markers */}
                            <div className="w-full border-t-2 border-dashed border-slate-600/50"></div>
                        </div>

                        {/* Track Steps (Grid) */}
                        <div className="absolute top-1/2 left-0 w-full h-20 -translate-y-1/2 flex justify-between px-12 pointer-events-none">
                            {[...Array(TRACK_LENGTH)].map((_, i) => (
                                <div key={i} className="w-px h-full bg-slate-700/50 flex flex-col justify-end items-center pb-2">
                                    <span className="text-[10px] text-slate-600">{i}</span>
                                </div>
                            ))}
                        </div>

                        {/* Finish Line */}
                        <div
                            className="absolute top-1/2 -translate-y-1/2 w-4 h-32 bg-white flex flex-col"
                            style={{
                                left: `${(level.goalSteps / TRACK_LENGTH) * 100}%`,
                                backgroundImage: 'repeating-linear-gradient(45deg, #000 25%, transparent 25%, transparent 75%, #000 75%, #000), repeating-linear-gradient(45deg, #000 25%, #fff 25%, #fff 75%, #000 75%, #000)',
                                backgroundPosition: '0 0, 10px 10px',
                                backgroundSize: '20px 20px'
                            }}
                        >
                            <div className="absolute -top-12 left-1/2 -translate-x-1/2 text-4xl">🏁</div>
                        </div>

                        {/* The Car */}
                        <motion.div
                            className="absolute top-1/2 -translate-y-1/2 text-6xl z-10"
                            animate={{
                                left: `${(carPosition / TRACK_LENGTH) * 100}%`,
                                rotate: status === 'crash' ? [0, -10, 10, -10, 0] : 0,
                                scale: status === 'crash' ? [1, 1.2, 0.8] : 1
                            }}
                            transition={{ type: "spring", stiffness: 100 }}
                            style={{ x: '-50%' }} // Center car on point
                        >
                            🏎️
                            {status === 'crash' && (
                                <motion.div
                                    className="absolute -top-4 -right-4 text-4xl"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1.5, opacity: 0 }}
                                >
                                    💥
                                </motion.div>
                            )}
                        </motion.div>
                    </div>

                    {/* Status Message */}
                    <div className="h-24 flex items-center justify-center">
                        <AnimatePresence mode='wait'>
                            {status === 'success' && (
                                <motion.div
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="bg-green-500 text-white px-8 py-4 rounded-2xl shadow-xl font-bold text-2xl flex items-center gap-3"
                                >
                                    <Trophy className="text-yellow-300" /> Race Won! Perfect Loop!
                                </motion.div>
                            )}
                            {status === 'crash' && (
                                <motion.div
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="bg-red-500 text-white px-8 py-4 rounded-2xl shadow-xl font-bold text-2xl"
                                >
                                    CRASH! Loop index out of bounds!
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                </div>
            </div>
        </div>
    );
};

const CodeIcon = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
    </svg>
);
